import type { Maybe } from '@adornis/base/utilTypes.js';
import { Field } from '@adornis/baseql/decorators.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';

/**
 * The last write Entra refused for a person, and what it was.
 *
 * ! A refusal is a state, not an event. !
 *
 * Without this every failed patch was a log row and nothing else, so the identical request went out
 * again on the next sync and on every sync after it - four a night for eight days, and it would have
 * continued forever. The two reasons a write is refused for good are both here: an address another
 * object in the tenant already holds, and a person whose Entra role outranks this application.
 *
 * The record is keyed by `field` because a person can be perfectly syncable in every respect except
 * one. Only that one field is skipped, and only until {@link value} changes - a new desired value has
 * never been tried and deserves its attempt.
 */
@Entity()
export class MicrosoftSyncRejection extends AdornisEntity {
  static override _class = 'MicrosoftSyncRejection';

  /** the property Entra refused, e.g. `mail` */
  @Field(type => String)
  field: Maybe<string>;

  /** what we tried to write. A different value clears the block. */
  @Field(type => String)
  value: Maybe<string>;

  /** Entra's error code, e.g. `Request_BadRequest` */
  @Field(type => String)
  code: Maybe<string>;

  /** Entra's message, shown to whoever has to fix it in the portal */
  @Field(type => String)
  message: Maybe<string>;

  @Field(type => Date, { default: v => v ?? new Date() })
  rejectedAt: Maybe<Date>;
}
